/* eslint-disable no-empty-function */
import Vue from "vue";
import VueRouter from "vue-router";
import store from "../store/index";
import { generateRoutes } from "@/utils/dynamicRoutes";
import { validateToken, clearUserInfo } from "@/utils/auth";

// 保存原型对象的Push
const originPush = VueRouter.prototype.push;
const originReplace = VueRouter.prototype.replace;

// 重写push方法
VueRouter.prototype.push = function (location, res, rej) {
  if (res && rej) {
    originPush.call(this, location, res, rej);
  } else {
    originPush.call(
      this,
      location,
      () => {},
      () => {}
    );
  }
};

// 重写replace方法
VueRouter.prototype.replace = function (location, res, rej) {
  if (res && rej) {
    originReplace.call(this, location, res, rej);
  } else {
    originReplace.call(
      this,
      location,
      () => {},
      () => {}
    );
  }
};

Vue.use(VueRouter);

// 基础路由（不需要登录就能访问）
export const baseRouter = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/main/login/login.vue"),
    meta: { noAuth: true, noVerify: true }
  },
  {
    path: "/register",
    name: "register",
    component: () => import("@/views/main/register/register.vue"),
    meta: { noAuth: true, noVerify: true }
  },
  {
    path: "/agreementPrivacy",
    name: "agreementPrivacy",
    component: () => import("@/views/main/login/agreementPrivacy.vue"),
    meta: { 
      noAuth: true, 
      noVerify: true,
      hidden: true,
      title: '法律协议'
    }
  },
  {
    path: "/404",
    name: "404",
    component: () => import("@/views/main/error/404.vue"),
    meta: { hidden: true, noAuth: true, noVerify: true }
  }
];

const createRouter = () => {
  return new VueRouter({
    mode: "history",
    routes: [...baseRouter],
  });
};

const router = createRouter();

// 重置路由
export function resetRouter() {
  try {
    console.log('🔄 重置路由');
    const newRouter = createRouter();
    
    router.matcher = newRouter.matcher;
  } catch (error) {
    console.error('❌ 重置路由失败:', error);
  }
}

// 添加动态路由
export function addRoutes(routes) {
  console.log('🔄 添加动态路由，数量:', routes.length);
  console.log('📋 动态路由详情:', routes.map(r => ({ path: r.path, name: r.name, hasComponent: !!r.component })));
  
  try {
    // 先重置路由
    resetRouter();
    
    // 创建主布局路由
    const layoutRoute = {
      path: '/',
      component: () => import('@/layout/home.vue'),
      redirect: '/dashboard',
      children: []
    };
    
    // 添加dashboard路由
    layoutRoute.children.push({
      path: 'dashboard',
      name: 'dashboard',
      component: () => import('@/views/main/dashboard/index.vue'),
      meta: {
        title: '首页',
        requiresAuth: true,
        icon: 'el-icon-house'
      }
    });
    
    // 添加密码修改路由
    layoutRoute.children.push({
      path: 'password',
      name: 'password',
      component: () => import('@/views/main/settingPassword/password.vue'),
      meta: {
        title: '修改密码',
        requiresAuth: true,
        hidden: true,
        icon: 'el-icon-lock'
      }
    });

    layoutRoute.children.push({
      path: 'adminProfile',
      name: 'adminProfile',
      component: () => import('@/views/main/adminProfile/adminProfile.vue'),
      meta: {
        title: '个人中心',
        requiresAuth: true,
        hidden: true,
        icon: 'el-icon-lock'
      }
    });
 

    layoutRoute.children.push({
      path: 'supplier-feed-history',
      name: 'SupplierFeedHistory',
      component: () => import('@/views/sysManage/supplier/supplierFeedHistory.vue'),
      meta: {
        title: '配料计划',
        requiresAuth: true,
        hidden: true
      }
    });

    layoutRoute.children.push({
      path: 'operationPassword',
      name: 'operationPassword',
      component: () => import('@/views/main/settingOperationPassword/operationPassword.vue'),
      meta: {
        title: '修改操作密码',
        requiresAuth: true,
        hidden: true,
        icon: 'el-icon-lock'
      }
    });
    layoutRoute.children.push({
      path: 'supplierAuthCode',
      name: 'supplierAuthCode',
      component: () => import('@/views/main/settingSupplierAuthCode/supplierAuthCode.vue'),
      meta: {
        title: '修改操作密码',
        requiresAuth: true,
        hidden: true,
        icon: 'el-icon-lock'
      }
    });
    
    
    
    // 添加其他路由
    routes.forEach(route => {
      // 确保路径正确
      const path = route.path.startsWith('/') ? route.path.slice(1) : route.path;
      
      // ⭐ 特别打印 customers 路由
      if (path === 'customers' || route.name === 'customers') {
        console.log('🎯 找到 customers 路由:', { ...route, path });
        console.log('🎯 customers 组件:', route.component);
      }
      
      layoutRoute.children.push({
        ...route,
        path: path
      });
    });
    
    // 添加主路由
    router.addRoute(layoutRoute);
    
    // ⭐ 打印当前所有路由
    console.log('📋 当前所有路由:', router.getRoutes().map(r => ({ name: r.name, path: r.path })));
    
    // 添加404路由（必须添加在通配符路由之前）
    router.addRoute({
      path: '/404',
      name: '404',
      component: () => import("@/views/main/error/404.vue"),
      meta: { hidden: true, noAuth: true, noVerify: true }
    });
    
    // 添加通配符路由（必须在最后）
    router.addRoute({
      path: '*',
      redirect: '/404'
    });
    
    console.log('✅ 路由添加完成');
    
  } catch (error) {
    console.error('❌ 添加路由失败:', error);
  }
}

// 获取用户类型（从sessionStorage或store）
function getUserType() {
  try {
    const userInfo = sessionStorage.getItem('userInfo');
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      return parsed.user_type || 'admin';
    }
  } catch (e) {
    console.error('获取用户类型失败:', e);
  }
  return 'admin';
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  console.log('🚦 路由守卫 ======================');
  console.log('从:', from.path || '初始');
  console.log('到:', to.path);
  
  // 检查是否是无需认证的页面
  if (to.matched.some(record => record.meta && record.meta.noAuth)) {
    console.log('✅ 无需认证页面，直接放行');
    return next();
  }
  
  const accessToken = sessionStorage.getItem("access-token");
  
  // 检查token
  if (!accessToken || !validateToken(accessToken)) {
    console.log('❌ Token无效，跳转到登录页');
    
    // 清理存储
    clearUserInfo();
    store.dispatch("user/logout");
    
    return next("/login");
  }
  
  // 已登录但访问登录/注册页，重定向到首页
  if (to.path === "/login" || to.path === "/register") {
    console.log('🔀 已登录，重定向到首页');
    return next("/");
  }
  
  // 检查是否需要加载动态路由
  if (!store.state.user.routesLoaded) {
    console.log('🔄 需要加载动态路由');
    
    try {
      // 从 sessionStorage 获取菜单
      const menuTreeStr = sessionStorage.getItem('menuTree');
      const menuTree = menuTreeStr ? JSON.parse(menuTreeStr) : [];
      console.log('📋 菜单树:', JSON.stringify(menuTree, null, 2));
      
      if (menuTree && menuTree.length > 0) {
        console.log('📋 获取到菜单树，数量:', menuTree.length);
        
        // ⭐ 打印所有菜单中的 component 字段
        const printMenuComponents = (menus, level = 0) => {
          menus.forEach(menu => {
            if (menu.menuType === 'C') {
              console.log(`${'  '.repeat(level)}📄 菜单: ${menu.menuName}, component: "${menu.component}", path: ${menu.path}`);
            }
            if (menu.children && menu.children.length) {
              printMenuComponents(menu.children, level + 1);
            }
          });
        };
        console.log('📋 菜单component详情:');
        printMenuComponents(menuTree);
        
        // 生成路由
        const routes = generateRoutes(menuTree);
        console.log('🔧 生成动态路由，数量:', routes.length);
        console.log('🔧 生成的路由详情:', routes.map(r => ({ path: r.path, name: r.name, hasComponent: !!r.component })));
        
        if (routes.length > 0) {
          // 添加动态路由
          addRoutes(routes);
          
          // 标记已加载
          store.commit('user/SET_ROUTES_LOADED', true);
          
          // 重新导航到目标页面
          return next({ ...to, replace: true });
        } else {
          console.warn('⚠️ 生成的路由为空');
          store.commit('user/SET_ROUTES_LOADED', true);
          return next('/dashboard');
        }
      } else {
        console.log('❌ 没有菜单数据，跳转到登录页');
        // 清理存储
        clearUserInfo();
        store.dispatch("user/logout");
        return next("/login");
      }
    } catch (error) {
      console.error('❌ 加载路由失败:', error);
      store.commit('user/SET_ROUTES_LOADED', true);
      // 出错时重定向到dashboard
      return next('/dashboard');
    }
  }
  
  console.log('✅ 路由检查通过，继续导航');
  next();
});

export default router;